import type { MCQConfig, PolarConfig, Question, Response } from "../lib/types";

type Surface = "dark" | "light";

interface Props {
  question: Question | null;
  response: Response;
  surface: Surface;
  /**
   * `"compact"` — used by the ticker and dense lists (smaller badge, tight gaps).
   * `"comfortable"` — used by the facilitator moderator (slightly larger).
   */
  size?: "compact" | "comfortable";
}

/**
 * Visual rendering of a participant's answer that knows about question type.
 * For open-text questions (or when no question is passed), falls back to
 * rendering `response.text` only — matching the pre-typed-question behaviour.
 *
 * For NPS: a coloured rating badge sits to the left of the "why" text.
 *   - 9–10 → Berry-filled "promoter" badge
 *   - 7–8  → outlined "passive" badge
 *   - 0–6  → muted "detractor" badge
 *
 * For MCQ/Polar: the chosen option/side is shown as the primary line; the
 * free-text (Sonstiges or per-option "warum") follows as a secondary line.
 */
export function AnswerDisplay({ question, response, surface, size = "compact" }: Props) {
  if (!question || question.question_type === "open_text") {
    return <PlainText text={response.text} surface={surface} />;
  }

  if (question.question_type === "nps") {
    return <NPSAnswer response={response} surface={surface} size={size} />;
  }

  if (question.question_type === "likert") {
    return <LikertAnswer response={response} surface={surface} size={size} />;
  }

  if (question.question_type === "multiple_choice") {
    return <MCQAnswer question={question} response={response} surface={surface} />;
  }

  if (question.question_type === "polar") {
    return <PolarAnswer question={question} response={response} surface={surface} />;
  }

  return <PlainText text={response.text} surface={surface} />;
}

// ─── Plain text (open_text fallback) ─────────────────────────────────────

function PlainText({ text, surface }: { text: string; surface: Surface }) {
  if (!text) return null;
  const cls = surface === "dark" ? "text-rc-paper/85" : "text-rc-black";
  return <p className={cls}>{text}</p>;
}

// ─── NPS ─────────────────────────────────────────────────────────────────

function bandForRating(r: number): "detractor" | "passive" | "promoter" {
  if (r <= 6) return "detractor";
  if (r <= 8) return "passive";
  return "promoter";
}

function NPSAnswer({
  response,
  surface,
  size,
}: {
  response: Response;
  surface: Surface;
  size: "compact" | "comfortable";
}) {
  const rating = response.rating;
  if (rating == null) {
    return <PlainText text={response.text} surface={surface} />;
  }
  const band = bandForRating(rating);
  const dim = size === "compact" ? "w-11 h-11 text-[18px]" : "w-12 h-12 text-[20px]";

  const badgeCls =
    surface === "dark"
      ? band === "promoter"
        ? "bg-rc-berry text-rc-paper border border-rc-berry shadow-berry"
        : band === "passive"
          ? "bg-transparent text-rc-paper border border-rc-paper/40"
          : "bg-rc-paper/5 text-rc-paper/70 border border-rc-paper/15"
      : band === "promoter"
        ? "bg-rc-berry text-rc-paper border border-rc-berry shadow-berry"
        : band === "passive"
          ? "bg-rc-paper text-rc-black border border-rc-charcoal/35"
          : "bg-rc-mist text-rc-slate border border-rc-fog";

  const textCls = surface === "dark" ? "text-rc-paper/85" : "text-rc-black";

  return (
    <div className="flex items-start gap-3 min-w-0">
      <span
        className={`flex-shrink-0 inline-flex items-center justify-center rounded-rc-md font-bold tabular-nums ${dim} ${badgeCls}`}
        aria-label={`Bewertung ${rating}`}
      >
        {rating}
      </span>
      {response.text ? (
        <p className={`flex-1 min-w-0 ${textCls}`}>{response.text}</p>
      ) : (
        <span className={`flex-1 min-w-0 italic ${surface === "dark" ? "text-rc-paper/40" : "text-rc-slate"}`}>
          {bandLabel(band)} · ohne Begründung
        </span>
      )}
    </div>
  );
}

function bandLabel(band: "detractor" | "passive" | "promoter"): string {
  if (band === "promoter") return "Promoter";
  if (band === "passive") return "Passiv";
  return "Detraktor";
}

// ─── Likert ──────────────────────────────────────────────────────────────

function likertBandForRating(r: number): "low" | "neutral" | "high" {
  if (r <= 2) return "low";
  if (r === 3) return "neutral";
  return "high";
}

function likertBandLabel(band: "low" | "neutral" | "high"): string {
  if (band === "high") return "Zustimmend";
  if (band === "neutral") return "Neutral";
  return "Kritisch";
}

function LikertAnswer({
  response,
  surface,
  size,
}: {
  response: Response;
  surface: Surface;
  size: "compact" | "comfortable";
}) {
  const rating = response.rating;
  if (rating == null) {
    return <PlainText text={response.text} surface={surface} />;
  }
  const band = likertBandForRating(rating);
  const dim = size === "compact" ? "w-11 h-11 text-[18px]" : "w-12 h-12 text-[20px]";

  const badgeCls =
    surface === "dark"
      ? band === "high"
        ? "bg-rc-berry text-rc-paper border border-rc-berry shadow-berry"
        : band === "neutral"
          ? "bg-transparent text-rc-paper border border-rc-paper/40"
          : "bg-rc-paper/5 text-rc-paper/70 border border-rc-paper/15"
      : band === "high"
        ? "bg-rc-berry text-rc-paper border border-rc-berry shadow-berry"
        : band === "neutral"
          ? "bg-rc-paper text-rc-black border border-rc-charcoal/35"
          : "bg-rc-mist text-rc-slate border border-rc-fog";

  const textCls = surface === "dark" ? "text-rc-paper/85" : "text-rc-black";
  const captionCls =
    surface === "dark" ? "text-rc-paper/55" : "text-rc-slate";

  return (
    <div className="flex items-start gap-3 min-w-0">
      <span
        className={`flex-shrink-0 inline-flex items-center justify-center rounded-rc-md font-bold tabular-nums ${dim} ${badgeCls}`}
        aria-label={`Bewertung ${rating} von 5`}
      >
        {rating}/5
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className={`text-[11px] font-bold uppercase tracking-[0.16em] ${captionCls}`}>
          {likertBandLabel(band)}
        </span>
        {response.text ? (
          <p className={textCls}>{response.text}</p>
        ) : (
          <span className={`italic ${surface === "dark" ? "text-rc-paper/40" : "text-rc-slate"}`}>
            ohne Begründung
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Multiple Choice ─────────────────────────────────────────────────────

function MCQAnswer({
  question,
  response,
  surface,
}: {
  question: Question;
  response: Response;
  surface: Surface;
}) {
  const cfg = question.config as MCQConfig;
  const label = response.is_other
    ? cfg.other_label || "Sonstiges"
    : cfg.options.find((o) => o.key === response.choice_key)?.label ?? "—";

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <Chip label={label} surface={surface} muted={response.is_other === true} />
      {response.text && <SecondaryText text={response.text} surface={surface} />}
    </div>
  );
}

// ─── Polar ───────────────────────────────────────────────────────────────

function PolarAnswer({
  question,
  response,
  surface,
}: {
  question: Question;
  response: Response;
  surface: Surface;
}) {
  const cfg = question.config as PolarConfig;
  const label =
    response.choice_key === cfg.side_a.key
      ? cfg.side_a.label
      : response.choice_key === cfg.side_b.key
        ? cfg.side_b.label
        : "—";

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <Chip label={label} surface={surface} />
      {response.text && <SecondaryText text={response.text} surface={surface} />}
    </div>
  );
}

// ─── Shared primitives ───────────────────────────────────────────────────

function Chip({
  label,
  surface,
  muted = false,
}: {
  label: string;
  surface: Surface;
  muted?: boolean;
}) {
  const base = "inline-flex self-start items-center px-3 py-1 rounded-full text-[13px] font-semibold leading-none";
  const cls =
    surface === "dark"
      ? muted
        ? `${base} bg-rc-paper/5 text-rc-paper/70 border border-rc-paper/15`
        : `${base} bg-rc-paper/10 text-rc-paper border border-rc-paper/25`
      : muted
        ? `${base} bg-rc-mist text-rc-slate border border-rc-fog`
        : `${base} bg-rc-paper text-rc-black border border-rc-charcoal/25`;
  return <span className={cls}>{label}</span>;
}

function SecondaryText({ text, surface }: { text: string; surface: Surface }) {
  const cls = surface === "dark" ? "text-rc-paper/75 text-[14px]" : "text-rc-charcoal text-[14px]";
  return <p className={cls}>{text}</p>;
}
